import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  LayoutDashboard, Users, Filter, Download, Search, RefreshCw,
  Phone, Mail, Calendar, Clock, ChevronDown, ChevronUp, 
  AlertCircle, CheckCircle, FileText, X, Clipboard, ArrowLeft,
  MessageSquare, History, ExternalLink, Building, Activity, LogOut
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Session storage keys - NO URL TOKENS
const TOKEN_STORAGE_KEY = 'admin_session_token';
const TOKEN_EXPIRY_KEY = 'admin_session_expiry';

// Business workflow statuses
const STATUSES = {
  NEW_INQUIRY: { label: 'New Inquiry', color: 'status-new', icon: AlertCircle },
  CONTACT_ATTEMPTED: { label: 'Contact Attempted', color: 'status-contacted', icon: Phone },
  AWAITING_RESPONSE: { label: 'Awaiting Response', color: 'status-awaiting', icon: Clock },
  INTAKE_STARTED: { label: 'Intake Started', color: 'status-intake', icon: FileText },
  INSURANCE_REVIEW: { label: 'Insurance Review', color: 'status-insurance', icon: Building },
  REFERRAL_NEEDED: { label: 'Referral Needed', color: 'status-referral', icon: ExternalLink },
  READY_TO_SCHEDULE: { label: 'Ready to Schedule', color: 'status-ready', icon: Calendar },
  SCHEDULED: { label: 'Scheduled', color: 'status-scheduled', icon: CheckCircle },
  FOLLOW_UP_NEEDED: { label: 'Follow-up Needed', color: 'status-followup', icon: RefreshCw },
  CLOSED: { label: 'Closed', color: 'status-closed', icon: X }
};

const AdminLeadsPage = () => {
  const navigate = useNavigate();
  
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [leads, setLeads] = useState([]);
  const [error, setError] = useState(null);
  
  // Filters
  const [filters, setFilters] = useState({
    type: 'all',
    status: 'all',
    search: '',
    dateRange: 'all'
  });
  
  // Selected lead for detail view
  const [selectedLead, setSelectedLead] = useState(null);
  const [newNote, setNewNote] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  // Get token from sessionStorage ONLY (no URL params)
  const getToken = useCallback(() => {
    return sessionStorage.getItem(TOKEN_STORAGE_KEY);
  }, []);

  // Check if token is expired
  const isTokenExpired = useCallback(() => {
    const expiry = sessionStorage.getItem(TOKEN_EXPIRY_KEY);
    if (!expiry) return true;
    return new Date(expiry) < new Date();
  }, []);

  // Create auth headers
  const getAuthHeaders = useCallback(() => {
    const token = getToken();
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    };
  }, [getToken]);

  // Logout function - clears all auth state
  const handleLogout = useCallback(async () => {
    const token = getToken();
    
    // Call logout endpoint
    if (token) {
      try {
        await fetch(`${API_URL}/api/admin/logout`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
      } catch (e) {
        // Ignore logout API errors
      }
    }
    
    // Clear all stored auth data
    sessionStorage.removeItem(TOKEN_STORAGE_KEY);
    sessionStorage.removeItem(TOKEN_EXPIRY_KEY);
    
    // Reset state
    setIsAuthenticated(false);
    setLeads([]);
    
    // Redirect to login
    navigate('/admin/login', { replace: true });
  }, [getToken, navigate]);

  // Verify session on mount
  useEffect(() => {
    const verifySession = async () => {
      const token = getToken();
      
      // No token - redirect to login
      if (!token) {
        navigate('/admin/login', { replace: true });
        return;
      }
      
      // Token expired - redirect to login
      if (isTokenExpired()) {
        sessionStorage.removeItem(TOKEN_STORAGE_KEY);
        sessionStorage.removeItem(TOKEN_EXPIRY_KEY);
        navigate('/admin/login', { replace: true });
        return;
      }
      
      // Verify token with backend
      try {
        const response = await fetch(`${API_URL}/api/admin/verify`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (!response.ok) {
          throw new Error('Invalid session');
        }
        
        setIsAuthenticated(true);
      } catch (e) {
        // Invalid token - clear and redirect
        sessionStorage.removeItem(TOKEN_STORAGE_KEY);
        sessionStorage.removeItem(TOKEN_EXPIRY_KEY);
        navigate('/admin/login', { replace: true });
      }
    };
    
    verifySession();
  }, [getToken, isTokenExpired, navigate]);

  // Fetch leads
  const fetchLeads = useCallback(async () => {
    if (!isAuthenticated) return;
    
    try {
      setIsLoading(true);
      const response = await fetch(`${API_URL}/api/admin/leads`, {
        headers: getAuthHeaders()
      });
      
      if (response.status === 401) {
        // Session expired
        handleLogout();
        return;
      }
      
      if (!response.ok) throw new Error('Failed to fetch leads');
      
      const data = await response.json();
      setLeads(data.leads || []);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated, getAuthHeaders, handleLogout]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchLeads();
    }
  }, [isAuthenticated, fetchLeads]);

  // Filter leads
  const filteredLeads = useMemo(() => {
    return leads.filter(lead => {
      if (filters.type !== 'all' && lead.lead_type !== filters.type) return false;
      if (filters.status !== 'all' && lead.status !== filters.status) return false;
      if (filters.search) {
        const search = filters.search.toLowerCase();
        const matchName = lead.name?.toLowerCase().includes(search);
        const matchEmail = lead.email?.toLowerCase().includes(search);
        const matchPhone = lead.phone?.includes(search);
        if (!matchName && !matchEmail && !matchPhone) return false;
      }
      return true;
    });
  }, [leads, filters]);

  // Stats
  const stats = useMemo(() => {
    const total = leads.length;
    const newInquiries = leads.filter(l => l.status === 'NEW_INQUIRY' || !l.status).length;
    const awaitingResponse = leads.filter(l => l.status === 'AWAITING_RESPONSE' || l.status === 'CONTACT_ATTEMPTED').length;
    const readyToSchedule = leads.filter(l => l.status === 'READY_TO_SCHEDULE').length;
    return { total, newInquiries, awaitingResponse, readyToSchedule };
  }, [leads]);

  // Update lead
  const updateLead = async (leadId, type, updates) => {
    try {
      setIsSaving(true);
      const response = await fetch(
        `${API_URL}/api/admin/leads/${type}/${leadId}`,
        {
          method: 'PATCH',
          headers: getAuthHeaders(),
          body: JSON.stringify({ ...updates, updated_by: 'Admin' })
        }
      );
      
      if (response.status === 401) {
        handleLogout();
        return;
      }
      
      if (!response.ok) throw new Error('Failed to update');
      
      const data = await response.json();
      
      // Update local state
      setLeads(prev => prev.map(l => 
        l.id === leadId ? { ...l, ...updates, updated_at: data.updated_at } : l
      ));
      
      if (selectedLead?.id === leadId) {
        setSelectedLead(prev => ({ ...prev, ...updates, updated_at: data.updated_at }));
      }
      
      setNewNote('');
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSaving(false);
    }
  };

  // Export CSV
  const exportCSV = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.type !== 'all') params.append('source', filters.type);
      if (filters.status !== 'all') params.append('lead_status', filters.status);
      
      const response = await fetch(`${API_URL}/api/admin/leads/export?${params.toString()}`, {
        headers: getAuthHeaders()
      });
      
      if (response.status === 401) {
        handleLogout();
        return;
      }
      
      if (!response.ok) throw new Error('Export failed');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `leads_export_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      setError('Export failed');
    }
  };

  // Copy to clipboard
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Show loading while verifying session
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw size={32} className="animate-spin text-teal-600 mx-auto mb-4" />
          <p className="text-slate-600">Verifying session...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Link to="/" className="flex items-center gap-2 text-slate-500 hover:text-slate-700">
                <ArrowLeft size={20} />
                <span className="hidden sm:inline text-sm">Back to Site</span>
              </Link>
              <div className="h-6 w-px bg-slate-200" />
              <div className="flex items-center gap-2">
                <LayoutDashboard size={20} className="text-teal-600" />
                <h1 className="font-semibold text-slate-900">Leads Dashboard</h1>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={fetchLeads}
                className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                title="Refresh"
                data-testid="btn-refresh"
              >
                <RefreshCw size={18} className={isLoading ? 'animate-spin' : ''} />
              </button>
              <button
                onClick={exportCSV}
                className="btn-secondary text-sm py-2 px-3 flex items-center gap-2"
                data-testid="btn-export"
              >
                <Download size={16} />
                <span className="hidden sm:inline">Export</span>
              </button>
              <button
                onClick={handleLogout}
                className="p-2 hover:bg-red-50 text-red-600 rounded-lg transition-colors"
                title="Logout"
                data-testid="btn-logout"
              >
                <LogOut size={18} />
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-xl p-5 border border-slate-200 shadow-sm" data-testid="stat-total">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-slate-500">Total Leads</span>
              <Users size={18} className="text-slate-400" />
            </div>
            <span className="text-2xl font-bold text-slate-900">{stats.total}</span>
          </div>
          <div className="bg-white rounded-xl p-5 border border-blue-200 shadow-sm" data-testid="stat-new">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-blue-600">New Inquiries</span>
              <AlertCircle size={18} className="text-blue-500" />
            </div>
            <span className="text-2xl font-bold text-blue-700">{stats.newInquiries}</span>
          </div>
          <div className="bg-white rounded-xl p-5 border border-amber-200 shadow-sm" data-testid="stat-awaiting">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-amber-600">Awaiting Response</span>
              <Clock size={18} className="text-amber-500" />
            </div>
            <span className="text-2xl font-bold text-amber-700">{stats.awaitingResponse}</span>
          </div>
          <div className="bg-white rounded-xl p-5 border border-teal-200 shadow-sm" data-testid="stat-ready">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-teal-600">Ready to Schedule</span>
              <Calendar size={18} className="text-teal-500" />
            </div>
            <span className="text-2xl font-bold text-teal-700">{stats.readyToSchedule}</span>
          </div>
        </div>

        {/* Filters Bar */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm mb-6">
          <div className="p-4 flex flex-col sm:flex-row gap-4">
            {/* Search */}
            <div className="flex-1 relative">
              <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                placeholder="Search by name, email, or phone..."
                value={filters.search}
                onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                className="w-full pl-10 pr-4 py-2.5 border border-slate-200 rounded-lg text-sm focus:border-teal-500 focus:ring-2 focus:ring-teal-100"
                data-testid="search-input"
              />
            </div>
            
            {/* Quick Filters */}
            <div className="flex gap-2">
              <select
                value={filters.type}
                onChange={(e) => setFilters(prev => ({ ...prev, type: e.target.value }))}
                className="px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white"
                data-testid="filter-type"
              >
                <option value="all">All Types</option>
                <option value="booking">Booking Requests</option>
                <option value="contact">Contact Inquiries</option>
              </select>
              
              <select
                value={filters.status}
                onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
                className="px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white"
                data-testid="filter-status"
              >
                <option value="all">All Statuses</option>
                {Object.entries(STATUSES).map(([key, { label }]) => (
                  <option key={key} value={key}>{label}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Error State */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6 flex items-center gap-3">
            <AlertCircle size={20} className="text-red-500" />
            <p className="text-red-700">{error}</p>
            <button onClick={() => setError(null)} className="ml-auto text-red-500 hover:text-red-700">
              <X size={18} />
            </button>
          </div>
        )}

        {/* Loading State */}
        {isLoading && (
          <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
            <RefreshCw size={32} className="animate-spin text-teal-600 mx-auto mb-4" />
            <p className="text-slate-600">Loading leads...</p>
          </div>
        )}

        {/* Empty State */}
        {!isLoading && filteredLeads.length === 0 && (
          <div className="bg-white rounded-xl border border-slate-200 p-12 text-center" data-testid="empty-state">
            <Users size={48} className="text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-900 mb-2">No Leads Found</h3>
            <p className="text-slate-600 mb-4">
              {filters.search || filters.type !== 'all' || filters.status !== 'all'
                ? 'No leads match your current filters.'
                : 'When visitors submit inquiries, they will appear here.'}
            </p>
            {(filters.search || filters.type !== 'all' || filters.status !== 'all') && (
              <button
                onClick={() => setFilters({ type: 'all', status: 'all', search: '', dateRange: 'all' })}
                className="btn-secondary"
              >
                Clear Filters
              </button>
            )}
          </div>
        )}

        {/* Leads List */}
        {!isLoading && filteredLeads.length > 0 && (
          <div className="space-y-4">
            {filteredLeads.map((lead) => {
              const statusConfig = STATUSES[lead.status] || STATUSES.NEW_INQUIRY;
              const StatusIcon = statusConfig.icon;
              
              return (
                <div
                  key={lead.id}
                  className={`bg-white rounded-xl border shadow-sm transition-all hover:shadow-md cursor-pointer
                    ${selectedLead?.id === lead.id ? 'border-teal-400 ring-2 ring-teal-100' : 'border-slate-200'}`}
                  onClick={() => setSelectedLead(selectedLead?.id === lead.id ? null : lead)}
                  data-testid={`lead-card-${lead.id}`}
                >
                  {/* Lead Header */}
                  <div className="p-4 sm:p-5">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center flex-shrink-0">
                          <span className="font-semibold text-slate-600">
                            {lead.name?.charAt(0)?.toUpperCase() || '?'}
                          </span>
                        </div>
                        <div>
                          <h3 className="font-semibold text-slate-900">{lead.name}</h3>
                          <div className="flex items-center gap-3 text-sm text-slate-500 mt-0.5">
                            <span className="flex items-center gap-1">
                              <Calendar size={14} />
                              {formatDate(lead.created_at)}
                            </span>
                            <span className="capitalize bg-slate-100 px-2 py-0.5 rounded text-xs">
                              {lead.lead_type}
                            </span>
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-3">
                        <span className={`status-badge ${statusConfig.color} flex items-center gap-1`}>
                          <StatusIcon size={12} />
                          {statusConfig.label}
                        </span>
                        {selectedLead?.id === lead.id ? (
                          <ChevronUp size={20} className="text-slate-400" />
                        ) : (
                          <ChevronDown size={20} className="text-slate-400" />
                        )}
                      </div>
                    </div>
                    
                    {/* Quick Contact */}
                    <div className="flex flex-wrap gap-4 mt-3 pt-3 border-t border-slate-100">
                      <a
                        href={`tel:${lead.phone}`}
                        onClick={(e) => e.stopPropagation()}
                        className="flex items-center gap-2 text-sm text-teal-700 hover:text-teal-800"
                      >
                        <Phone size={14} />
                        {lead.phone}
                      </a>
                      <a
                        href={`mailto:${lead.email}`}
                        onClick={(e) => e.stopPropagation()}
                        className="flex items-center gap-2 text-sm text-teal-700 hover:text-teal-800"
                      >
                        <Mail size={14} />
                        {lead.email}
                      </a>
                    </div>
                  </div>

                  {/* Expanded Details */}
                  {selectedLead?.id === lead.id && (
                    <div className="border-t border-slate-200 p-4 sm:p-5 bg-slate-50" onClick={(e) => e.stopPropagation()}>
                      <div className="grid md:grid-cols-2 gap-6">
                        {/* Lead Details */}
                        <div>
                          <h4 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                            <FileText size={16} className="text-teal-600" />
                            Lead Details
                          </h4>
                          <dl className="space-y-2 text-sm">
                            {lead.service_interest && (
                              <div className="flex justify-between">
                                <dt className="text-slate-500">Service Interest:</dt>
                                <dd className="text-slate-900 font-medium">{lead.service_interest}</dd>
                              </div>
                            )}
                            {lead.payment_type && (
                              <div className="flex justify-between">
                                <dt className="text-slate-500">Payment Type:</dt>
                                <dd className="text-slate-900 font-medium capitalize">{lead.payment_type.replace('_', ' ')}</dd>
                              </div>
                            )}
                            {lead.referral_source && (
                              <div className="flex justify-between">
                                <dt className="text-slate-500">Referral Source:</dt>
                                <dd className="text-slate-900 font-medium capitalize">{lead.referral_source.replace('_', ' ')}</dd>
                              </div>
                            )}
                            {lead.message && (
                              <div className="pt-2">
                                <dt className="text-slate-500 mb-1">Message:</dt>
                                <dd className="text-slate-900 bg-white p-3 rounded-lg border border-slate-200 text-sm">
                                  {lead.message}
                                </dd>
                              </div>
                            )}
                          </dl>

                          {/* Quick Actions */}
                          <div className="flex flex-wrap gap-2 mt-4">
                            <a
                              href={`tel:${lead.phone}`}
                              className="btn-primary text-sm py-2 px-3 flex items-center gap-1"
                            >
                              <Phone size={14} />
                              Call
                            </a>
                            <button
                              onClick={() => copyToClipboard(lead.email)}
                              className="btn-secondary text-sm py-2 px-3 flex items-center gap-1"
                            >
                              <Clipboard size={14} />
                              Copy Email
                            </button>
                            <button
                              onClick={() => copyToClipboard(
                                `Name: ${lead.name}\nPhone: ${lead.phone}\nEmail: ${lead.email}\nService: ${lead.service_interest || 'N/A'}`
                              )}
                              className="btn-secondary text-sm py-2 px-3 flex items-center gap-1"
                            >
                              <FileText size={14} />
                              Copy Summary
                            </button>
                          </div>
                        </div>

                        {/* Status & Notes */}
                        <div>
                          <h4 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                            <Activity size={16} className="text-teal-600" />
                            Status & Notes
                          </h4>
                          
                          {/* Status Selector */}
                          <div className="mb-4">
                            <label className="text-sm text-slate-600 block mb-1">Update Status</label>
                            <select
                              value={lead.status || 'NEW_INQUIRY'}
                              onChange={(e) => updateLead(lead.id, lead.lead_type, { status: e.target.value })}
                              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white"
                              disabled={isSaving}
                              data-testid={`status-select-${lead.id}`}
                            >
                              {Object.entries(STATUSES).map(([key, { label }]) => (
                                <option key={key} value={key}>{label}</option>
                              ))}
                            </select>
                          </div>

                          {/* Notes */}
                          <div className="mb-4">
                            <label className="text-sm text-slate-600 block mb-1">Internal Notes</label>
                            <textarea
                              value={newNote}
                              onChange={(e) => setNewNote(e.target.value)}
                              placeholder="Add a note..."
                              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm resize-none"
                              rows={3}
                              data-testid={`note-input-${lead.id}`}
                            />
                            <button
                              onClick={() => {
                                if (newNote.trim()) {
                                  const existingNote = lead.internal_note || '';
                                  const timestamp = new Date().toLocaleString();
                                  const updatedNote = `[${timestamp}] ${newNote}\n${existingNote}`;
                                  updateLead(lead.id, lead.lead_type, { internal_note: updatedNote });
                                }
                              }}
                              disabled={!newNote.trim() || isSaving}
                              className="btn-primary text-sm py-2 mt-2"
                            >
                              {isSaving ? 'Saving...' : 'Add Note'}
                            </button>
                          </div>

                          {/* Existing Notes */}
                          {lead.internal_note && (
                            <div className="bg-white border border-slate-200 rounded-lg p-3 text-sm">
                              <h5 className="font-medium text-slate-700 mb-2 flex items-center gap-1">
                                <History size={14} />
                                Notes History
                              </h5>
                              <pre className="whitespace-pre-wrap text-slate-600 text-xs font-mono">
                                {lead.internal_note}
                              </pre>
                            </div>
                          )}

                          {/* Last Updated */}
                          {lead.updated_at && (
                            <p className="text-xs text-slate-500 mt-3">
                              Last updated: {formatDate(lead.updated_at)}
                              {lead.updated_by && ` by ${lead.updated_by}`}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* DEMO Badge */}
        <div className="fixed bottom-4 right-4 bg-amber-500 text-white px-4 py-2 rounded-full text-sm font-semibold shadow-lg">
          DEMO MODE
        </div>
      </main>
    </div>
  );
};

export default AdminLeadsPage;
